import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import PatientWard1 from "../assets/images/Patient-Ward/Patient-ward-1.webp";
import PatientWard2 from "../assets/images/Patient-Ward/Patient-ward-2.jpg";
import PatientWard3 from "../assets/images/Patient-Ward/Patient-ward-3.jpg";
import PatientWard4 from "../assets/images/Patient-Ward/Patient-ward-4.jpg";
import PatientWard5 from "../assets/images/Patient-Ward/Patient-ward-5.jpg";
import PatientWard6 from "../assets/images/Patient-Ward/Patient-ward-6.jpg";
import PatientWard7 from "../assets/images/Patient-Ward/Patient-ward-7.jpg";
import PatientWard8 from "../assets/images/Patient-Ward/Patient-ward-8.jpg";
import PatientWard9 from "../assets/images/Patient-Ward/Patient-ward-9.jpg";
import SEO from "../components/SEO";

/**
 * Patient Ward Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Bed Config),
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL ="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const wards = [
  {
    name: "General Ward — 6-Bed Open Bay",
    zone: "Level 1",
    size: "60 sq.m",
    beds: "6 Beds",
    tag: "General",
    note: "Shared open bay with curtain partitions, central nurse-call panel, attached toilet block.",
    image: PatientWard1,
  },
  {
    name: "Semi-Private Ward — 2-Bed Room",
    zone: "Level 1",
    size: "22 sq.m",
    beds: "2 Beds",
    tag: "Semi-Private",
    note: "Shared room with privacy curtain, individual oxygen points, attendant seating.",
    image: PatientWard2,
  },
  {
    name: "Private Single Patient Room",
    zone: "Level 2",
    size: "16 sq.m",
    beds: "1 Bed",
    tag: "Private",
    note: "Ensuite bathroom, recliner for attendant, wall-mounted entertainment unit.",
    image: PatientWard3,
  },
  {
    name: "Deluxe Suite Ward Room",
    zone: "Level 2",
    size: "28 sq.m",
    beds: "1 Bed + Lounge",
    tag: "Deluxe",
    note: "Separate lounge zone, premium finishes, dedicated AC unit, mini pantry for attendants.",
    image: PatientWard4,
  },
  {
    name: "Pediatric Ward Bay",
    zone: "Level 1",
    size: "40 sq.m / 4 beds",
    beds: "4 Beds",
    tag: "Pediatric",
    note: "Child-friendly murals, rounded furniture edges, dedicated play corner, parent recliners.",
    image: PatientWard5,
  },
  {
    name: "Maternity Postnatal Ward",
    zone: "Level 1",
    size: "24 sq.m",
    beds: "2 Beds",
    tag: "Maternity",
    note: "Rooming-in bassinet bays, breastfeeding screen, soft ambient lighting.",
    image: PatientWard6,
  },
  {
    name: "Isolation Ward Room",
    zone: "Level 2",
    size: "20 sq.m",
    beds: "1 Bed",
    tag: "Isolation",
    note: "Anteroom with hand-hygiene station, negative-pressure option, dedicated exhaust.",
    image: PatientWard7,
  },
  {
    name: "Geriatric Care Ward",
    zone: "Level 1",
    size: "26 sq.m",
    beds: "2 Beds",
    tag: "Geriatric",
    note: "Low-height beds, grab rails throughout, non-slip flooring, wider door clearances.",
    image: PatientWard8,
  },
  {
    name: "Day-Care Observation Ward",
    zone: "Level 1",
    size: "50 sq.m / 8 bays",
    beds: "8 Bays",
    tag: "Day-Care",
    note: "Recliner-style observation bays, quick-turnover layout, shared nursing pod.",
    image: PatientWard9,
  },
];

function SpecLine({ zone, size, beds }) {
  return (
    <div
      style={{
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{beds}</span>
    </div>
  );
}

function WardCard({ w, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {w.image ? (
          <img
            src={w.image}
            alt={w.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {w.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {w.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {w.note}
        </p>
        <SpecLine zone={w.zone} size={w.size} beds={w.beds} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(w)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PatientWardDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedWard, setSelectedWard] = useState(null);

  const handleGetQuote = (ward) => {
    setSelectedWard(ward);
    setShowConsultation(true);
  };

  return (
    <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .ward-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .ward-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .ward-grid-3x3 { grid-template-columns: 1fr; } }
        .ward-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .ward-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .ward-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="ward-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {wards.map((w, i) => (
            <WardCard key={i} w={w} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="ward-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="ward-modal-inner">
            <button
              className="ward-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedWard} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}